import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Upload,
  FileText,
  User,
  Building2,
  Calendar,
  CheckCircle,
  AlertCircle,
  Loader2,
  Search,
  UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';

export const ReportUploadPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [departments, setDepartments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [orders, setOrders] = useState([]);
  const [searchPatient, setSearchPatient] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [reportContent, setReportContent] = useState('');
  const [uploadMode, setUploadMode] = useState('pdf'); // 'pdf', 'text', or 'image'
  
  const [formData, setFormData] = useState({
    report_type: 'pathology', // Default to Pathology
    dept_id: '',
    patient_id: '',
    patient_name: '',
    patient_mrn: '',
    patient_age: '',
    patient_sex: '',
    clinical_context: '',
    image_type: 'chest_xray',
    report_date: new Date().toISOString().split('T')[0],
    order_id: '',
    assigned_clinician_id: '',
    isNewPatient: false, // Track if patient is newly created from search
  });
  
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [highlightedPatientIndex, setHighlightedPatientIndex] = useState(-1);

  // Auto-select department based on report type
  useEffect(() => {
    if (departments.length > 0 && formData.report_type) {
      // Map report type to department code
      const deptCodeMap = {
        'pathology': 'PATH',
        'laboratory': 'LAB',
        'radiology': 'RAD'
      };
      
      const targetCode = deptCodeMap[formData.report_type];
      if (targetCode) {
        // Find department matching the code (case-insensitive)
        const matchingDept = departments.find(d => 
          d.dept_code?.toUpperCase() === targetCode ||
          d.dept_name?.toUpperCase().includes(formData.report_type.toUpperCase())
        );
        
        if (matchingDept && formData.dept_id !== matchingDept.dept_id) {
          setFormData(prev => ({ ...prev, dept_id: matchingDept.dept_id }));
        }
      }
    }
  }, [formData.report_type, departments]);

  // Auto-select upload method based on report type
  useEffect(() => {
    if (formData.report_type === 'radiology') {
      setUploadMode('image');
    } else if (formData.report_type === 'laboratory' || formData.report_type === 'pathology') {
      setUploadMode('pdf');
    }
    // Clear selected file when switching modes
    setSelectedFile(null);
  }, [formData.report_type]);

  // Auto-select current user as clinician when clinicians list loads
  useEffect(() => {
    if (clinicians.length > 0 && user?.user_id && !formData.assigned_clinician_id) {
      // Check if current user is in the clinicians list
      const currentUserAsClinician = clinicians.find(c => c.user_id === user.user_id);
      if (currentUserAsClinician) {
        setFormData(prev => ({ ...prev, assigned_clinician_id: currentUserAsClinician.user_id }));
      } else if (clinicians.length > 0) {
        // If current user is not a clinician, select the first available clinician
        setFormData(prev => ({ ...prev, assigned_clinician_id: clinicians[0].user_id }));
      }
    }
  }, [clinicians, user]);

  useEffect(() => {
    fetchDepartments();
    fetchPatients();
    fetchPendingOrders();
    fetchClinicians();
  }, []);

  // Note: Clinician dropdown should NOT filter by department
  // Clinicians can receive reports from any department

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      // Handle paginated response format
      setDepartments(response.data?.departments || response.data || []);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const fetchClinicians = async () => {
    try {
      // For doctors/admins, fetch cross-org; for technicians, only same org
      const isTechnician = user?.role === 'technician';
      let url = '/users/clinicians';
      if (!isTechnician) url += '?cross_org=true';
      
      const response = await api.get(url);
      setClinicians(response.data?.clinicians || []);
    } catch (error) {
      console.error('Failed to fetch clinicians:', error);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      // Handle paginated response format
      setPatients(response.data?.patients || response.data || []);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
    }
  };

  const fetchPendingOrders = async () => {
    try {
      const response = await api.get('/orders?status=in_progress');
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // Validate patient selection before allowing file upload
  const validatePatientBeforeUpload = () => {
    const hasExistingPatient = formData.patient_id && !formData.isNewPatient;
    const hasNewPatient = formData.isNewPatient && formData.patient_name?.trim();
    
    if (!hasExistingPatient && !hasNewPatient) {
      toast.error('Please select or add a patient before uploading a document.', {
        description: 'Search for an existing patient or type a new name and press Enter to create one.',
        duration: 5000,
      });
      return false;
    }
    return true;
  };

  // Handle click on file upload area - validate patient first
  const handleFileUploadClick = (e, inputId) => {
    if (!validatePatientBeforeUpload()) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // Patient is valid, trigger the file input
    document.getElementById(inputId)?.click();
  };

  const handlePatientSelect = (patientId) => {
    const patient = patients.find(p => p.patient_id === patientId);
    if (patient) {
      setFormData(prev => ({
        ...prev,
        patient_id: patientId,
        patient_name: patient.full_name,
        patient_mrn: patient.mrn_member_id || '',
        isNewPatient: false,
      }));
      setSearchPatient('');
      setShowPatientDropdown(false);
      setHighlightedPatientIndex(-1);
    }
  };

  // Generate a random MRN for new patients
  const generateRandomMRN = () => {
    const timestamp = Date.now().toString(36).toUpperCase();
    const randomPart = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `MRN-${timestamp}-${randomPart}`;
  };

  // Create a new patient from the search text when no match found
  const handleCreateNewPatient = (patientName) => {
    if (!patientName || patientName.trim().length === 0) return;
    
    const trimmedName = patientName.trim();
    const generatedMRN = generateRandomMRN();
    
    setFormData(prev => ({
      ...prev,
      patient_id: '', // No existing patient_id for new patients
      patient_name: trimmedName,
      patient_mrn: generatedMRN, // Auto-generate MRN for new patients
      isNewPatient: true,
    }));
    setSearchPatient('');
    setShowPatientDropdown(false);
    setHighlightedPatientIndex(-1);
  };

  // Handle keyboard events for patient search
  const handlePatientSearchKeyDown = (e) => {
    const visiblePatients = filteredPatients.slice(0, 5);
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedPatientIndex(prev => 
        prev < visiblePatients.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedPatientIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedPatientIndex >= 0 && visiblePatients[highlightedPatientIndex]) {
        // Select highlighted existing patient
        handlePatientSelect(visiblePatients[highlightedPatientIndex].patient_id);
      } else if (searchPatient.trim() && visiblePatients.length === 0) {
        // No matches - create new patient
        handleCreateNewPatient(searchPatient);
      } else if (searchPatient.trim() && highlightedPatientIndex === -1) {
        // Has input but nothing highlighted and there are results - create new patient
        handleCreateNewPatient(searchPatient);
      }
    } else if (e.key === 'Tab') {
      // Tab behavior: select highlighted or create new
      if (highlightedPatientIndex >= 0 && visiblePatients[highlightedPatientIndex]) {
        e.preventDefault();
        handlePatientSelect(visiblePatients[highlightedPatientIndex].patient_id);
      } else if (searchPatient.trim() && visiblePatients.length === 0) {
        // No matches - create new patient
        handleCreateNewPatient(searchPatient);
      }
      // If there are matches but nothing highlighted, just let tab move focus naturally
      setShowPatientDropdown(false);
    } else if (e.key === 'Escape') {
      setShowPatientDropdown(false);
      setHighlightedPatientIndex(-1);
    }
  };

  const handleOrderSelect = (orderId) => {
    const order = orders.find(o => o.order_id === orderId);
    if (order) {
      setFormData(prev => ({
        ...prev,
        order_id: orderId,
        patient_id: order.patient_id,
        patient_name: order.patient_name || '',
        report_type: order.order_type,
        dept_id: order.dept_id,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.report_type || !formData.dept_id) {
      toast.error('Please select report type and department');
      return;
    }
    
    // Patient validation: need either existing patient_id OR new patient (isNewPatient with patient_name)
    const hasExistingPatient = formData.patient_id && !formData.isNewPatient;
    const hasNewPatient = formData.isNewPatient && formData.patient_name?.trim();
    
    if (!hasExistingPatient && !hasNewPatient) {
      toast.error('Please select or add a patient before uploading a document.', {
        description: 'Search for an existing patient or type a new name and press Enter to create one.',
        duration: 5000,
      });
      return;
    }

    if (!formData.assigned_clinician_id) {
      toast.error('Please select a clinician to receive this report');
      return;
    }

    // PDF Upload Mode
    if (uploadMode === 'pdf' && selectedFile) {
      // Allow both PDF and image files for Lab Reports
      const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.webp'];
      const fileExt = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
      if (!allowedExtensions.includes(fileExt)) {
        toast.error('Please select a PDF or image file (JPEG, PNG, WEBP)');
        return;
      }
      
      setLoading(true);
      setProcessing(true);
      
      // Start progress animation immediately
      setProcessingMessage('Uploading PDF...');
      setProcessingProgress(5);
      
      // Continuous smooth progress animation - ALWAYS keeps moving
      let currentProgress = 5;
      let maxProgress = 95; // Never exceed 95% until truly complete
      const smoothProgressInterval = setInterval(() => {
        if (currentProgress < maxProgress) {
          // Always increment slowly, speed varies based on how close to max
          const distanceToMax = maxProgress - currentProgress;
          // Faster when far from max, slower when close
          const baseIncrement = distanceToMax > 30 ? 0.8 : distanceToMax > 15 ? 0.4 : 0.15;
          currentProgress = Math.min(currentProgress + baseIncrement, maxProgress);
          setProcessingProgress(Math.round(currentProgress));
        }
      }, 150);
      
      // Helper to update status message (progress keeps moving on its own)
      const updateStatusMessage = (message) => {
        if (message) setProcessingMessage(message);
      };
      
      try {
        const formDataObj = new FormData();
        formDataObj.append('file', selectedFile);
        formDataObj.append('report_type', formData.report_type);
        formDataObj.append('dept_id', formData.dept_id);
        formDataObj.append('report_date', formData.report_date);
        formDataObj.append('assigned_clinician_id', formData.assigned_clinician_id);
        if (formData.patient_id) formDataObj.append('patient_id', formData.patient_id);
        if (formData.patient_name) formDataObj.append('patient_name', formData.patient_name);
        if (formData.patient_mrn) formDataObj.append('patient_mrn', formData.patient_mrn);
        if (formData.order_id) formDataObj.append('order_id', formData.order_id);
        // Flag for new patient creation
        if (formData.isNewPatient) formDataObj.append('is_new_patient', 'true');
        
        // Use async upload endpoint to avoid timeout
        const response = await api.post('/reports/upload-pdf-async', formDataObj);
        
        if (response.data?.job_id) {
          // Poll for completion
          const jobId = response.data.job_id;
          const reportId = response.data.report_id;
          
          // Update status message
          updateStatusMessage('Extracting text from PDF...');
          
          // Poll for job completion
          let attempts = 0;
          const maxAttempts = 80; // ~2 minutes max polling
          const pollInterval = 1500; // Poll every 1.5 seconds
          
          const pollStatus = async () => {
            attempts++;
            try {
              const statusResponse = await api.get(`/reports/upload-status/${jobId}`);
              const status = statusResponse.data?.status;
              const progress = statusResponse.data?.progress;
              
              // Only update the status message, progress bar keeps moving on its own
              if (progress) {
                if (progress.includes('Extracting text') || progress.includes('Queued')) {
                  updateStatusMessage('Extracting text from PDF...');
                } else if (progress.includes('Running AI') || progress.includes('analysis')) {
                  updateStatusMessage('AI is analyzing your report...');
                } else if (progress.includes('Saving')) {
                  updateStatusMessage('Saving results...');
                } else if (progress.includes('Complete')) {
                  updateStatusMessage('Processing complete!');
                }
              }
              
              if (status === 'completed') {
                const result = statusResponse.data?.result;
                clearInterval(smoothProgressInterval);
                setProcessingProgress(100);
                setProcessingMessage('Processing complete!');
                
                // Brief delay to show 100% before navigating
                setTimeout(() => {
                  setProcessingMessage('');
                  setProcessingProgress(0);
                  setLoading(false);
                  setProcessing(false);
                  toast.success(`Report processed! ${result?.observations_extracted || 0} lab values extracted.`, {
                    duration: 4000,
                  });
                  // Redirect to Report Detail page
                  navigate(`/reports/${reportId}`);
                }, 400);
                return;
              } else if (status === 'failed') {
                clearInterval(smoothProgressInterval);
                const errorMsg = statusResponse.data?.error || 'Unknown error';
                setProcessingMessage('');
                setProcessingProgress(0);
                setLoading(false);
                setProcessing(false);
                if (errorMsg.includes('Budget') || errorMsg.includes('budget')) {
                  toast.error('AI processing failed: Budget limit exceeded. Please contact admin.', { duration: 8000 });
                } else {
                  toast.error(`AI processing failed: ${errorMsg}. Report saved.`, { duration: 8000 });
                }
                navigate(`/reports/${reportId}`);
                return;
              } else if (attempts >= maxAttempts) {
                clearInterval(smoothProgressInterval);
                setProcessingMessage('');
                setProcessingProgress(0);
                setLoading(false);
                setProcessing(false);
                toast.warning('Processing is taking longer than expected. Check the report page for status.', { duration: 6000 });
                navigate(`/reports/${reportId}`);
                return;
              }
              
              // Continue polling
              setTimeout(pollStatus, pollInterval);
            } catch (pollError) {
              console.error('Poll error:', pollError);
              if (attempts >= maxAttempts) {
                clearInterval(smoothProgressInterval);
                setProcessingMessage('');
                setProcessingProgress(0);
                setLoading(false);
                setProcessing(false);
                toast.warning('Could not verify processing status. Check the report page.', { duration: 6000 });
                navigate(`/reports/${reportId}`);
              } else {
                setTimeout(pollStatus, pollInterval);
              }
            }
          };
          
          // Start polling
          setTimeout(pollStatus, pollInterval);
        } else {
          // Fallback for unexpected response
          toast.warning('Upload response unclear. Navigating to reports...', { duration: 4000 });
          navigate('/reports');
        }
      } catch (error) {
        // Handle Pydantic validation errors
        const errorData = error.response?.data;
        let errorMessage = 'Failed to upload PDF';
        
        if (errorData?.detail) {
          if (Array.isArray(errorData.detail)) {
            // Pydantic validation error - extract field names and messages
            errorMessage = errorData.detail.map(e => {
              const fieldName = e.loc && e.loc.length > 1 ? e.loc[e.loc.length - 1] : 'field';
              const msg = e.msg || e.message || 'validation error';
              return `${fieldName}: ${msg}`;
            }).join('; ');
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (typeof errorData.detail === 'object') {
            errorMessage = errorData.detail.msg || errorData.detail.message || JSON.stringify(errorData.detail);
          }
        }
        
        toast.error(errorMessage);
        // Only reset states on error - polling handles success/completion
        clearInterval(smoothProgressInterval);
        setLoading(false);
        setProcessing(false);
        setProcessingMessage('');
        setProcessingProgress(0);
      }
      return;
    }

    // Radiology Image Upload Mode
    if (uploadMode === 'image') {
      // Require file in radiology mode
      if (!selectedFile) {
        toast.error('Please select a radiology file (image or PDF) to upload');
        return;
      }
      
      // Allow both image and PDF files for Radiology
      const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.pdf'];
      const fileExt = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
      if (!allowedExtensions.includes(fileExt)) {
        toast.error('Please select an image file (JPEG, PNG, WEBP) or PDF');
        return;
      }
      
      setLoading(true);
      setProcessing(true);
      setProcessingProgress(5);
      setProcessingMessage('Uploading radiology image...');
      
      // Start smooth progress animation for radiology upload
      let currentProgress = 5;
      let maxProgress = 95;
      const smoothProgressInterval = setInterval(() => {
        if (currentProgress < maxProgress) {
          const distanceToMax = maxProgress - currentProgress;
          const baseIncrement = distanceToMax > 30 ? 0.8 : distanceToMax > 15 ? 0.4 : 0.15;
          currentProgress = Math.min(currentProgress + baseIncrement, maxProgress);
          setProcessingProgress(Math.round(currentProgress));
        }
      }, 150);
      
      try {
        const formDataObj = new FormData();
        formDataObj.append('file', selectedFile);
        formDataObj.append('image_type', formData.image_type || 'chest_xray');
        formDataObj.append('dept_id', formData.dept_id);
        formDataObj.append('report_date', formData.report_date);
        formDataObj.append('assigned_clinician_id', formData.assigned_clinician_id);
        if (formData.patient_id) formDataObj.append('patient_id', formData.patient_id);
        if (formData.patient_name) formDataObj.append('patient_name', formData.patient_name);
        if (formData.patient_mrn) formDataObj.append('patient_mrn', formData.patient_mrn);
        if (formData.patient_age) formDataObj.append('patient_age', formData.patient_age);
        if (formData.patient_sex) formDataObj.append('patient_sex', formData.patient_sex);
        if (formData.clinical_context) formDataObj.append('clinical_context', formData.clinical_context);
        if (formData.order_id) formDataObj.append('order_id', formData.order_id);
        // Flag for new patient creation
        if (formData.isNewPatient) formDataObj.append('is_new_patient', 'true');
        
        const response = await api.post('/reports/upload-radiology-image', formDataObj);
        
        // Stop progress animation
        clearInterval(smoothProgressInterval);
        setProcessingProgress(100);
        setProcessingMessage('Analysis complete!');
        
        const processingResult = response.data?.processing;
        if (processingResult?.auto_processed) {
          const urgentMsg = processingResult.urgent_findings ? ' URGENT FINDINGS DETECTED!' : '';
          toast.success(`Radiology image analyzed!${urgentMsg} ${processingResult.findings_count} findings detected. Quality: ${processingResult.image_quality}`, {
            duration: 6000,
          });
          navigate(`/reports/${response.data.report_id}`);
        } else if (processingResult?.error) {
          const errorMsg = processingResult.error;
          if (errorMsg.includes('Budget') || errorMsg.includes('budget')) {
            toast.error('AI analysis failed: Budget limit exceeded. Please contact admin.', { duration: 8000 });
          } else {
            toast.error(`AI analysis failed: ${errorMsg}. Image saved - you can retry later.`, { duration: 8000 });
          }
          navigate(`/reports/${response.data.report_id}`);
        } else {
          toast.warning('Image uploaded but AI analysis did not complete.', { duration: 6000 });
          navigate(`/reports/${response.data.report_id}`);
        }
      } catch (error) {
        // Handle Pydantic validation errors (array of {type, loc, msg, input, url})
        const errorData = error.response?.data;
        let errorMessage = 'Failed to upload radiology image';
        
        if (errorData?.detail) {
          if (Array.isArray(errorData.detail)) {
            // Pydantic validation error - extract field names and messages for clarity
            errorMessage = errorData.detail.map(e => {
              const fieldName = e.loc && e.loc.length > 1 ? e.loc[e.loc.length - 1] : 'field';
              const msg = e.msg || e.message || 'validation error';
              return `${fieldName}: ${msg}`;
            }).join('; ');
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (typeof errorData.detail === 'object') {
            errorMessage = errorData.detail.msg || errorData.detail.message || JSON.stringify(errorData.detail);
          }
        }
        
        toast.error(errorMessage);
      } finally {
        clearInterval(smoothProgressInterval);
        setLoading(false);
        setProcessing(false);
        setProcessingProgress(0);
      }
      return;
    }

    // Text Content Mode
    const hasContent = reportContent.trim().length > 0;
    const shouldAutoProcess = hasContent;

    if (!hasContent && uploadMode === 'text') {
      toast.error('Please paste report content for AI processing');
      return;
    }

    setLoading(true);
    if (shouldAutoProcess) setProcessing(true);
    
    try {
      const params = new URLSearchParams({
        report_type: formData.report_type,
        dept_id: formData.dept_id,
        report_date: formData.report_date,
        auto_process: shouldAutoProcess ? 'true' : 'false',
      });
      
      if (formData.patient_id) params.append('patient_id', formData.patient_id);
      if (formData.patient_name) params.append('patient_name', formData.patient_name);
      if (formData.patient_mrn) params.append('patient_mrn', formData.patient_mrn);
      if (formData.order_id) params.append('order_id', formData.order_id);
      if (selectedFile) params.append('file_name', selectedFile.name);
      // Flag for new patient creation
      if (formData.isNewPatient) params.append('is_new_patient', 'true');
      
      if (hasContent) {
        params.append('report_text_content', reportContent);
      }

      const response = await api.post(`/reports/upload?${params.toString()}`);
      
      const processingResult = response.data?.processing;
      if (processingResult?.auto_processed) {
        toast.success(`Report processed successfully! ${processingResult.observations_extracted} lab values extracted. Urgency: ${processingResult.urgency_level}`, {
          duration: 5000,
        });
      } else if (hasContent) {
        toast.success('Report uploaded! AI processing may take a moment.');
      } else {
        toast.success('Report uploaded. Paste report content to enable AI analysis.');
      }
      
      // Navigate to dashboard after successful upload
      navigate('/');
    } catch (error) {
      // Handle Pydantic validation errors
      const errorData = error.response?.data;
      let errorMessage = 'Failed to upload report';
      
      if (errorData?.detail) {
        if (Array.isArray(errorData.detail)) {
          // Pydantic validation error - extract field names and messages
          errorMessage = errorData.detail.map(e => {
            const fieldName = e.loc && e.loc.length > 1 ? e.loc[e.loc.length - 1] : 'field';
            const msg = e.msg || e.message || 'validation error';
            return `${fieldName}: ${msg}`;
          }).join('; ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.msg || errorData.detail.message || JSON.stringify(errorData.detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setProcessing(false);
    }
  };

  const filteredPatients = patients.filter(p => 
    p.full_name?.toLowerCase().includes(searchPatient.toLowerCase()) ||
    p.mrn_member_id?.toLowerCase().includes(searchPatient.toLowerCase())
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="report-upload-page">
      {/* Processing Overlay */}
      {processing && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <Card className="w-96 shadow-2xl">
            <CardContent className="p-8 text-center">
              {/* Animated Progress Bar */}
              <div className="w-full bg-slate-200 rounded-full h-3 mb-6 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-primary to-primary/70 h-3 rounded-full transition-all duration-300 ease-out relative"
                  style={{ width: `${processingProgress}%` }}
                >
                  {/* Shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                </div>
              </div>
              
              {/* Status Message */}
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {processingMessage || 'Processing Report'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {processingProgress < 30 && 'Preparing your document...'}
                {processingProgress >= 30 && processingProgress < 70 && 'AI is analyzing the report content...'}
                {processingProgress >= 70 && processingProgress < 95 && 'Generating clinical insights...'}
                {processingProgress >= 95 && 'Almost done...'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Upload Report</h1>
        <p className="text-muted-foreground mt-1">
          Upload a completed diagnostic report for processing and review
        </p>
      </div>

      {/* Pending Orders Section */}
      {orders.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              Pending Orders
            </CardTitle>
            <CardDescription>
              Select an existing order to link this report
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {orders.slice(0, 4).map((order) => (
                <div
                  key={order.order_id}
                  onClick={() => handleOrderSelect(order.order_id)}
                  className={`p-3 border rounded-lg cursor-pointer transition-all ${
                    formData.order_id === order.order_id 
                      ? 'border-primary bg-primary/5 ring-2 ring-primary' 
                      : 'border-slate-200 hover:border-primary/50 bg-white'
                  }`}
                  data-testid={`order-${order.order_id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <Badge variant="outline" className="capitalize">{order.order_type}</Badge>
                    <Badge className={order.priority === 'stat' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}>
                      {order.priority}
                    </Badge>
                  </div>
                  <p className="font-medium text-sm">{order.patient_name}</p>
                  <p className="text-xs text-muted-foreground">MRN: {order.patient_mrn || 'N/A'}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Report Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Report Type & Department */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="report_type">Report Type *</Label>
                <Select
                  value={formData.report_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, report_type: value }))}
                >
                  <SelectTrigger data-testid="report-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="laboratory">Laboratory</SelectItem>
                    <SelectItem value="pathology">Pathology</SelectItem>
                    <SelectItem value="radiology">Radiology</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dept_id">Department *</Label>
                <Select
                  value={formData.dept_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, dept_id: value }))}
                >
                  <SelectTrigger data-testid="department-select">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((dept) => (
                      <SelectItem key={dept.dept_id} value={dept.dept_id}>
                        {dept.dept_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Patient Selection */}
            <div className="space-y-3">
              <Label>Patient Information *</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or MRN..."
                  value={searchPatient}
                  onChange={(e) => {
                    setSearchPatient(e.target.value);
                    setShowPatientDropdown(true);
                    setHighlightedPatientIndex(-1);
                    // Clear any previously selected patient when typing
                    if (formData.patient_id || formData.isNewPatient) {
                      setFormData(prev => ({
                        ...prev,
                        patient_id: '',
                        patient_name: '',
                        patient_mrn: '',
                        isNewPatient: false,
                      }));
                    }
                  }}
                  onFocus={() => setShowPatientDropdown(true)}
                  onBlur={() => {
                    // Delay hiding to allow click on dropdown items
                    // Using longer delay for cross-browser compatibility (Windows Chrome)
                    setTimeout(() => setShowPatientDropdown(false), 300);
                  }}
                  onKeyDown={handlePatientSearchKeyDown}
                  className="pl-10"
                  data-testid="patient-search"
                />
              </div>
              
              {/* Patient dropdown - show when searching */}
              {showPatientDropdown && searchPatient && (
                <div className="border rounded-lg max-h-48 overflow-y-auto shadow-md">
                  {filteredPatients.length > 0 ? (
                    <>
                      {filteredPatients.slice(0, 5).map((patient, index) => (
                        <div
                          key={patient.patient_id}
                          onMouseDown={(e) => {
                            // Use onMouseDown instead of onClick for cross-browser compatibility
                            // onMouseDown fires before onBlur, ensuring selection works on Windows Chrome
                            e.preventDefault();
                            handlePatientSelect(patient.patient_id);
                          }}
                          onMouseEnter={() => setHighlightedPatientIndex(index)}
                          className={`p-3 cursor-pointer border-b last:border-b-0 transition-colors ${
                            highlightedPatientIndex === index 
                              ? 'bg-primary/10' 
                              : 'hover:bg-slate-50'
                          }`}
                          data-testid={`patient-option-${patient.patient_id}`}
                        >
                          <p className="font-medium text-sm">{patient.full_name}</p>
                          <p className="text-xs text-muted-foreground">
                            MRN: {patient.mrn_member_id || 'N/A'} | DOB: {patient.dob ? new Date(patient.dob).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      ))}
                      {/* Option to create new patient even when there are partial matches */}
                      <div
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleCreateNewPatient(searchPatient);
                        }}
                        className="p-3 cursor-pointer bg-blue-50 hover:bg-blue-100 border-t transition-colors flex items-center gap-2"
                        data-testid="create-new-patient-option"
                      >
                        <UserPlus className="w-4 h-4 text-blue-600" />
                        <div>
                          <p className="font-medium text-sm text-blue-800">Create new patient: &ldquo;{searchPatient.trim()}&rdquo;</p>
                          <p className="text-xs text-blue-600">Press Enter to add as new patient</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    /* No matches - show create new patient option */
                    <div
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleCreateNewPatient(searchPatient);
                      }}
                      className="p-4 cursor-pointer bg-blue-50 hover:bg-blue-100 transition-colors"
                      data-testid="create-new-patient-no-match"
                    >
                      <div className="flex items-center gap-3">
                        <UserPlus className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="font-medium text-sm text-blue-800">No matching patients found</p>
                          <p className="text-sm text-blue-700 mt-1">
                            Press <kbd className="px-1.5 py-0.5 bg-blue-200 rounded text-xs font-mono">Enter</kbd> or <kbd className="px-1.5 py-0.5 bg-blue-200 rounded text-xs font-mono">Tab</kbd> to create new patient: <strong>&ldquo;{searchPatient.trim()}&rdquo;</strong>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Selected existing patient card */}
              {formData.patient_id && !formData.isNewPatient && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div className="flex-1">
                    <p className="font-medium text-sm text-green-800">{formData.patient_name}</p>
                    <p className="text-xs text-green-700">MRN: {formData.patient_mrn || 'N/A'}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        patient_id: '',
                        patient_name: '',
                        patient_mrn: '',
                        isNewPatient: false,
                      }));
                      setSearchPatient('');
                    }}
                    className="text-xs text-green-700 hover:text-green-900 underline"
                    data-testid="clear-patient-btn"
                  >
                    Change
                  </button>
                </div>
              )}

              {/* New patient card - same style as existing patient for consistency */}
              {formData.isNewPatient && formData.patient_name && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                  <div className="flex-1">
                    <p className="font-medium text-sm text-blue-800">{formData.patient_name}</p>
                    <p className="text-xs text-blue-600">
                      New Patient • MRN: {formData.patient_mrn} (will be created on submission)
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        patient_id: '',
                        patient_name: '',
                        patient_mrn: '',
                        isNewPatient: false,
                      }));
                      setSearchPatient('');
                    }}
                    className="text-xs text-blue-700 hover:text-blue-900 underline"
                    data-testid="clear-new-patient-btn"
                  >
                    Change
                  </button>
                </div>
              )}

              {/* HIDDEN: Manual patient entry section - kept for future re-enabling */}
              {/* 
              {!formData.patient_id && !formData.isNewPatient && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="space-y-2">
                    <Label htmlFor="patient_name">Patient Name</Label>
                    <Input
                      id="patient_name"
                      value={formData.patient_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, patient_name: e.target.value }))}
                      placeholder="Enter patient name"
                      data-testid="patient-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="patient_mrn">Patient MRN</Label>
                    <Input
                      id="patient_mrn"
                      value={formData.patient_mrn}
                      onChange={(e) => setFormData(prev => ({ ...prev, patient_mrn: e.target.value }))}
                      placeholder="Enter MRN"
                      data-testid="patient-mrn-input"
                    />
                  </div>
                </div>
              )}
              */}
            </div>

            {/* Clinician Selection - Required */}
            <div className="space-y-2">
              <Label htmlFor="assigned_clinician">Assign to Clinician *</Label>
              <Select
                value={formData.assigned_clinician_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, assigned_clinician_id: value }))}
              >
                <SelectTrigger data-testid="clinician-select">
                  <SelectValue placeholder="Select clinician to receive this report" />
                </SelectTrigger>
                <SelectContent>
                  {clinicians.map((clinician) => (
                    <SelectItem key={clinician.user_id} value={clinician.user_id}>
                      <div className="flex flex-col">
                        <span>{clinician.full_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {clinician.org_name}{clinician.dept_name ? ` • ${clinician.dept_name}` : ''}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {user?.role === 'technician' 
                  ? 'Select a clinician within your organization'
                  : 'You can assign to any clinician across organizations'}
              </p>
            </div>

            {/* Report Date */}
            <div className="space-y-2">
              <Label htmlFor="report_date">Report Date</Label>
              <Input
                id="report_date"
                type="date"
                value={formData.report_date}
                onChange={(e) => setFormData(prev => ({ ...prev, report_date: e.target.value }))}
                data-testid="report-date-input"
              />
            </div>

            {/* Upload Mode Toggle */}
            <div className="space-y-3">
              <Label>Upload Method</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={uploadMode === 'pdf' ? 'default' : 'outline'}
                  onClick={() => setUploadMode('pdf')}
                  className="flex-1"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Lab Report
                </Button>
                <Button
                  type="button"
                  variant={uploadMode === 'image' ? 'default' : 'outline'}
                  onClick={() => { setUploadMode('image'); setFormData(prev => ({...prev, report_type: 'radiology'})); }}
                  className="flex-1"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  X-Ray/Image
                </Button>
                <Button
                  type="button"
                  variant={uploadMode === 'text' ? 'default' : 'outline'}
                  onClick={() => setUploadMode('text')}
                  className="flex-1"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Paste Text
                </Button>
              </div>
            </div>

            {/* Radiology Image Upload Mode */}
            {uploadMode === 'image' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Image Type</Label>
                  <Select
                    value={formData.image_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, image_type: value }))}
                  >
                    <SelectTrigger data-testid="image-type-select">
                      <SelectValue placeholder="Select image type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="chest_xray">Chest X-Ray</SelectItem>
                      <SelectItem value="bone_xray">Bone X-Ray</SelectItem>
                      <SelectItem value="abdominal_xray">Abdominal X-Ray</SelectItem>
                      <SelectItem value="ct_scan">CT Scan</SelectItem>
                      <SelectItem value="mri">MRI</SelectItem>
                      <SelectItem value="ultrasound">Ultrasound</SelectItem>
                      <SelectItem value="mammography">Mammography</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="patient_age">Patient Age</Label>
                    <Input
                      id="patient_age"
                      value={formData.patient_age}
                      onChange={(e) => setFormData(prev => ({ ...prev, patient_age: e.target.value }))}
                      placeholder="e.g., 45 years"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Patient Sex</Label>
                    <Select
                      value={formData.patient_sex}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, patient_sex: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clinical_context">Clinical Context / Reason for Exam</Label>
                  <Textarea
                    id="clinical_context"
                    value={formData.clinical_context}
                    onChange={(e) => setFormData(prev => ({ ...prev, clinical_context: e.target.value }))}
                    placeholder="e.g., Cough for 2 weeks, rule out pneumonia. History of smoking."
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Radiology File</Label>
                  <div 
                    onClick={(e) => handleFileUploadClick(e, 'image-upload')}
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                      selectedFile ? 'border-green-300 bg-green-50' : 'border-slate-200 hover:border-primary/50'
                    }`}
                  >
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="image-upload"
                      data-testid="image-upload-input"
                    />
                    <div>
                      {selectedFile ? (
                        <div className="flex items-center justify-center gap-3">
                          <CheckCircle className="w-8 h-8 text-green-600" />
                          <div className="text-left">
                            <p className="font-medium text-sm text-green-800">{selectedFile.name}</p>
                            <p className="text-xs text-green-600">
                              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB - Ready for AI analysis
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                          <p className="font-medium">Click to upload X-Ray, medical image or PDF</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            JPEG, PNG, WEBP or PDF (max 50MB)
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-amber-800">
                    <p className="font-medium">AI Analysis Disclaimer</p>
                    <p className="mt-1">This AI analysis is for decision support only and should be reviewed by a qualified radiologist. Do not use for final diagnosis.</p>
                  </div>
                </div>
              </div>
            )}

            {/* PDF Upload Mode */}
            {uploadMode === 'pdf' && (
              <div className="space-y-2">
                <Label>Report File</Label>
                <div 
                  onClick={(e) => handleFileUploadClick(e, 'file-upload')}
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                    selectedFile ? 'border-green-300 bg-green-50' : 'border-slate-200 hover:border-primary/50'
                  }`}
                >
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                    data-testid="file-upload-input"
                  />
                  <div>
                    {selectedFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                        <div className="text-left">
                          <p className="font-medium text-sm text-green-800">{selectedFile.name}</p>
                          <p className="text-xs text-green-600">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB - Ready for processing
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                        <p className="font-medium">Click to upload report file</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          PDF or image files (JPEG, PNG, WEBP) - max 50MB
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-blue-800">
                    <p className="font-medium">Automatic Text Extraction</p>
                    <p className="mt-1">Text will be automatically extracted from the file and processed by AI to generate lab values, SOAP notes, and patient summary.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Text Content Mode */}
            {uploadMode === 'text' && (
              <div className="space-y-2">
                <Label htmlFor="report_content" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Report Content
                </Label>
                <Textarea
                  id="report_content"
                  value={reportContent}
                  onChange={(e) => setReportContent(e.target.value)}
                  placeholder="Paste the lab report content here...

Example:
COMPLETE BLOOD COUNT
Hemoglobin: 12.5 g/dL (Reference: 12.0-16.0)
WBC: 11.2 x10^9/L (Reference: 4.0-10.0) HIGH"
                  className="min-h-[180px] font-mono text-sm"
                  data-testid="report-content-input"
                />
                <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-blue-800">
                    <p className="font-medium">AI Processing</p>
                    <p className="mt-1">The AI will extract test values, generate SOAP notes, and create a patient-friendly summary.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/reports')}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading || (uploadMode === 'pdf' && !selectedFile) || (uploadMode === 'image' && !selectedFile)} 
                data-testid="submit-upload-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {processing ? 'Processing with AI...' : 'Uploading...'}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadMode === 'pdf' ? 'Upload PDF & Process' : 
                     uploadMode === 'image' ? 'Upload Image & Analyze' : 
                     'Upload & Process Report'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
